import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@root/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  private formatErrors(error: any) {
    return throwError(() => error.error);
  }

  get(path: string): Observable<any>;
  get<T>(path: string): Observable<T>;
  get(path: string, params: HttpParams | Record<string, unknown>): Observable<any>;
  get<T>(path: string, params: HttpParams | Record<string, unknown>): Observable<T>;

  get<T>(
    path: string,
    params: HttpParams | Record<string, unknown> = new HttpParams(),
  ): Observable<T> {
    return this.http
      .get<T>(environment.api.url + path, {
        params: params instanceof HttpParams ? params : ApiService.toHttpParams(params),
      })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * Like `get`, but lets the `HttpErrorResponse` through instead of unwrapping it to the response
   * body, so a caller can tell a refusal from any other failure.
   */
  getRaw<T>(
    path: string,
    params: Record<string, unknown> = {},
    context?: HttpContext,
  ): Observable<T> {
    return this.http.get<T>(environment.api.url + path, {
      params: ApiService.toHttpParams(params),
      context,
    });
  }

  /**
   * Builds HttpParams from a plain object, skipping `null`/`undefined` values so
   * absent filters don't get serialized as the literal string "undefined"/"null".
   */
  private static toHttpParams(obj: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item !== null && item !== undefined) {
            httpParams = httpParams.append(key, String(item));
          }
        }
      } else {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return httpParams;
  }

  put(path: string, body: Object = {}): Observable<any> {
    return this.http.put(environment.api.url + path, body).pipe(catchError(this.formatErrors));
  }

  post<T>(path: string): Observable<T>;
  post<T>(path: string, body: Object): Observable<T>;
  post<T>(path: string, body: Object, options: Object): Observable<T>;
  post(path: string, body: Object = {}, options: Object = {}): Observable<any> {
    return this.http
      .post(environment.api.url + path, body, options)
      .pipe(catchError(this.formatErrors));
  }

  /**
   * Like `post`, but lets the `HttpErrorResponse` through instead of unwrapping it to the
   * response body. The auth flows need the status code to tell outcomes apart (a 404 from
   * the reset-request endpoint is deliberately treated as success, a 400 from the
   * set-password endpoint means the link is dead), and `formatErrors` throws that away.
   */
  postRaw<T>(path: string, body: object = {}, options: object = {}): Observable<T> {
    return this.http.post<T>(environment.api.url + path, body, options);
  }

  patch(path: string, body: Object = {}, context?: HttpContext): Observable<any> {
    return this.http
      .patch(environment.api.url + path, body, { context })
      .pipe(catchError(this.formatErrors));
  }

  delete(path: string): Observable<Object> {
    return this.http.delete(environment.api.url + path).pipe(catchError(this.formatErrors));
  }

  // The raw writes below let the `HttpErrorResponse` through, like `getRaw`, for a caller that
  // explains each refusal by its status and reason.
  putRaw<T>(path: string, body: object, context?: HttpContext): Observable<T> {
    return this.http.put<T>(environment.api.url + path, body, { context });
  }

  patchRaw<T>(path: string, body: object, context?: HttpContext): Observable<T> {
    return this.http.patch<T>(environment.api.url + path, body, { context });
  }

  // Some routes read a body on DELETE, which `delete` cannot send.
  deleteRaw<T>(path: string, body: object, context?: HttpContext): Observable<T> {
    return this.http.delete<T>(environment.api.url + path, { body, context });
  }

  /** Like `getImgBlob`, but keeps response headers (e.g. Content-Disposition) alongside the
   * blob body — needed when the filename comes from the server rather than being known upfront. */
  getBlobResponse(path: string): Observable<HttpResponse<Blob>> {
    return this.http
      .get(environment.api.url + path, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.formatErrors));
  }

  getImgBlob(path: string): Observable<any> {
    return this.http
      .get(environment.api.url + path, {
        responseType: 'blob',
      })
      .pipe(catchError(this.formatErrors));
  }

  getText(path: string): Observable<any> {
    return this.http
      .get(environment.api.url + path, {
        responseType: 'text',
      })
      .pipe(catchError(this.formatErrors));
  }
}
